import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { UX_THEME_V5_ENABLED } from '../../config/themeAccess';
import { IconMoon, IconSun } from '../../icons';

/**
 * ThemeToggle — TopBar-Umschalter Dark/Hell (M-D5 Welle 3 W3-1,
 * `plan-ux-d5-w3-light-flip.md` §2/§4).
 *
 * Self-gated (Outer/Inner-Gate-Muster, Bestandskonvention — vgl.
 * `KanzleiUebergabeMonthEndBanner`): rendert `null`, solange
 * {@link UX_THEME_V5_ENABLED} `false` ist, sodass Aufrufer die Komponente
 * unconditional einbinden können und Flag-off byte-identisch bleibt (kein
 * Umschalter im DOM). Der Flag-Check läuft in der ÄUSSEREN Komponente VOR
 * jedem Hook-Aufruf — `useTheme()` wird nur von `ThemeToggleInner` gerufen,
 * die ausschließlich bei Flag ON gemountet wird. Das ist zugleich funktional
 * notwendig: bestehende MainLayout-Testsuiten rendern `<MainLayout />` ohne
 * `<ThemeProvider>`-Wrapper (Flag ist dort aus) — würde `useTheme()`
 * unconditional in dieser Komponente laufen, würfe es fail-closed, obwohl
 * die Komponente wegen des Flags ohnehin `null` rendern soll.
 *
 * A11y: `<button>` mit `aria-pressed` (true == Light aktiv) und sichtbarem
 * Fokus-Ring aus dem globalen `button:focus-visible`-Token (`--accent`,
 * `index.css:148-155`) — kein Extra-Ring-Styling nötig.
 *
 * @module components/common/ThemeToggle
 */
const ThemeToggle: React.FC = () => {
  if (!UX_THEME_V5_ENABLED) {
    return null;
  }
  return <ThemeToggleInner />;
};

const ThemeToggleInner: React.FC = () => {
  const { t } = useTranslation('common');
  const { theme, setTheme } = useTheme();

  const isLight = theme === 'light';
  const label = isLight
    ? t('theme.switchToDark', { defaultValue: 'Dunkles Design aktivieren' })
    : t('theme.switchToLight', { defaultValue: 'Helles Design aktivieren' });

  return (
    <button
      type="button"
      onClick={() => setTheme(isLight ? 'dark' : 'light')}
      aria-pressed={isLight}
      aria-label={label}
      title={label}
      className="flex items-center justify-center w-8 h-8 rounded-md text-secondary hover:text-primary hover:bg-surface-hover transition-colors"
    >
      {isLight ? <IconSun aria-hidden /> : <IconMoon aria-hidden />}
    </button>
  );
};

export default ThemeToggle;
